import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { Loader2, Plus, Download, Trash2, QrCode } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUGGESTIONS = ["QR - Gas Stations", "QR - Uber Drivers", "QR - MetroLink", "QR - Airport", "QR - TikTok Bio"];

function QrTile({ c, onDelete }) {
  const wrapRef = useRef(null);
  const download = () => {
    const canvas = wrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.download = `${c.slug}-qr.png`;
    a.href = canvas.toDataURL("image/png");
    a.click();
    toast.success("QR code downloaded — print it anywhere.");
  };
  return (
    <div data-testid={`qr-tile-${c.slug}`} className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5 flex flex-col items-center text-center">
      <div className="text-white font-semibold mb-3">{c.name}</div>
      <div ref={wrapRef} className="bg-white p-3 rounded-lg"><QRCodeCanvas value={c.url} size={150} level="M" /></div>
      <div className="text-white/35 text-[10px] mt-2 break-all px-2">{c.url}</div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 mt-4 text-sm w-full">
        <div className="text-white/45 text-xs text-right">Scans</div><div className="text-white font-semibold text-left">{c.clicks}</div>
        <div className="text-white/45 text-xs text-right">Leads</div><div className="text-white font-semibold text-left">{c.leads}</div>
        <div className="text-white/45 text-xs text-right">Bookings</div><div className="text-white font-semibold text-left">{c.bookings}</div>
        <div className="text-white/45 text-xs text-right">Revenue</div><div className="text-[#F2B705] font-semibold text-left">${Number(c.revenue).toLocaleString()}</div>
        <div className="text-white/45 text-xs text-right">ROI</div><div className={`font-semibold text-left ${c.roi > 0 ? "text-green-400" : "text-white/50"}`}>{c.roi != null ? `${c.roi}%` : "—"}</div>
      </div>
      <div className="flex gap-2 mt-4">
        <button data-testid={`qr-download-${c.slug}`} onClick={download} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#F2B705] text-black font-bold text-xs"><Download size={13} /> Download PNG</button>
        <button data-testid={`qr-delete-${c.slug}`} onClick={() => onDelete(c)} className="p-1.5 rounded-md bg-white/5 border border-white/10 text-white/50 hover:text-red-400"><Trash2 size={13} /></button>
      </div>
    </div>
  );
}

export default function QrGenerator({ token }) {
  const [rows, setRows] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const H = { headers: { Authorization: `Bearer ${token}` } };

  const load = () => axios.get(`${API}/marketing/campaigns`, H).then((r) => setRows(r.data.filter((c) => c.kind === "qr")));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const create = async (n) => {
    const label = (n || name).trim();
    if (!label) { toast.error("Give the QR code a name."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/marketing/campaigns`, { name: label, kind: "qr", channel: "QR Code" }, H);
      toast.success(`QR campaign "${label}" created.`);
      setName("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create QR code."); }
    finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? Printed codes will still open the site but stop being tracked to this campaign.`)) return;
    await axios.delete(`${API}/marketing/campaigns/${c.id}`, H);
    load();
  };

  if (!rows) return <div className="flex items-center gap-2 text-white/50 py-16 justify-center"><Loader2 className="animate-spin" size={18} /> Loading QR codes…</div>;

  return (
    <div data-testid="qr-generator" className="space-y-5">
      <div className="bg-[#0C0C0C] border border-[#F2B705]/25 rounded-xl p-5">
        <h3 className="font-display font-bold text-white flex items-center gap-2 mb-1"><QrCode size={17} className="text-[#F2B705]" /> QR Campaign Generator</h3>
        <p className="text-white/50 text-sm mb-4">Every flyer, business card, car decal, or bio link gets its own QR code — so you know exactly which one produced the rental.</p>
        <div className="flex gap-2">
          <input data-testid="qr-name-input" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} placeholder="QR - Gas Stations" className="flex-1 bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2 text-white text-sm" />
          <button data-testid="qr-create-btn" onClick={() => create()} disabled={busy} className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#F2B705] text-black font-bold text-sm disabled:opacity-50"><Plus size={15} /> Create</button>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {SUGGESTIONS.filter((s) => !rows.some((r) => r.name === s)).map((s) => (
            <button key={s} data-testid={`qr-suggest-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => create(s)} className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-white/60 text-xs hover:border-[#F2B705]/50">+ {s}</button>
          ))}
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {rows.map((c) => <QrTile key={c.id} c={c} onDelete={remove} />)}
        {rows.length === 0 && <div className="md:col-span-3 text-center text-white/40 py-10">No QR campaigns yet — create your first one above.</div>}
      </div>
    </div>
  );
}
